'use client'

import { type ISbStoryData, storyblokEditable } from '@storyblok/js'
import cn from 'clsx'
import { useLenis } from 'lenis/react'
import { usePathname } from 'next/navigation'
import { useEffect, useRef, useState } from 'react'
import type { StoryblokRichtext } from 'storyblok-rich-text-react-renderer'
import AELABS from '~/assets/ae-labs.svg'
import { Button } from '~/components/button'
import { Form, Input, SubmitButton, validateEmail } from '~/components/form'
import { Image } from '~/components/image'
import { Link } from '~/components/link'
import { TerminalAnimation } from '~/components/terminal-writing'
import { RichText } from '~/integrations/storyblok/renderer'
import { useStore } from '~/libs/store'
import { navigationAction } from './action'
import s from './navigation.module.css'

type SBBlock = {
  _uid: string
  _editable: string
}

type SBLink = SBBlock & {
  text: string
  url: string
  icon: {
    filename: string
    alt: string
  }
}

export function NavigationComponent({
  className,
  story,
}: { className?: string; story: ISbStoryData }) {
  const { isNavOpened, setIsNavOpened } = useStore()
  const { content } = story

  return (
    <nav className={cn('dt:sticky h-full', s.nav, className)}>
      <button
        type="button"
        className={cn(
          'mobile-only fixed inset-0 bg-black z-1 opacity-0 transition-opacity duration-300 pointer-events-none',
          isNavOpened && 'opacity-75 pointer-events-auto'
        )}
        onClick={() => setIsNavOpened(false)}
      />
      <div
        className={cn(s.inner, isNavOpened && s.open)}
        data-lenis-prevent
        {...storyblokEditable(content)}
      >
        <Borders className="desktop-only" />
        <div
          className={cn(
            'flex flex-col justify-start uppercase p dt:dr-p-20 overflow-y-auto',
            s.content
          )}
        >
          <div className="flex items-center justify-between dr-mb-32 dt:dr-mb-50">
            <Link href="/" className="dr-w-169 dr-h-35 dt:dr-mb-10">
              <AELABS className="dt:mx-auto" />
            </Link>
            <button
              type="button"
              className={cn(
                s.hamburger,
                'dr-w-21 aspect-square dt:hidden flex flex-col dr-gap-3 justify-between',
                isNavOpened && s.open
              )}
              onClick={() => setIsNavOpened(!isNavOpened)}
            >
              <span className="bg-[color:theme('colors.blue')] dr-w-full dr-h-3" />
              <span className="bg-[color:theme('colors.blue')] dr-w-full dr-h-3" />
              <span className="bg-[color:theme('colors.blue')] dr-w-full dr-h-3" />
            </button>
          </div>
          <div className="flex flex-col ">
            <Image
              className="dr-w-195! dr-h-136! dr-mb-12 dt:dr-h-auto dt:w-full! mx-auto"
              priority
              // objectFit="contain"
              src={content?.asciiArt?.filename}
              alt={content?.asciiArt?.alt ?? ''}
            />

            <p className="dr-mb-10">
              {'>'} {content?.asciiHeader}
            </p>

            <MenuCalendar />

            <div className="flex flex-col dt:dr-mb-30 dr-gap-6">
              <p className="dt:dr-mb-4">{'> PAGES:'}</p>
              {content?.links?.map(({ text, url, _uid, _editable }: SBLink) => (
                <Button
                  key={_uid}
                  href={url}
                  onClick={() => setIsNavOpened(false)}
                  {...storyblokEditable({ _editable })}
                >
                  {text}
                </Button>
              ))}
            </div>

            <Footer story={story} className="desktop-only" />
          </div>
        </div>
      </div>
    </nav>
  )
}

function mapHeaderText(text: string) {
  return [
    {
      _uid: '1',
      _editable: '1',
      lines: [{ line: text, _uid: '1', _editable: '1' }],
    },
  ]
}

export function Header({
  className,
  story,
}: { className?: string; story: ISbStoryData }) {
  const pathname = usePathname()
  const { content } = story
  const [headerText, setHeaderText] = useState(
    mapHeaderText(content?.topHeader)
  )

  useEffect(() => {
    setHeaderText(mapHeaderText(content?.topHeader))
  }, [content?.topHeader])

  return (
    <header
      className={cn(
        'relative dt:sticky dt:dr-top-18 w-full dt:dr-h-75 bg-primary z-1 dt:dr-mb-20',
        className,
        s.header
      )}
    >
      <Borders className="desktop-only" />
      <div
        className={cn(
          'dt:dr-p-22 flex items-center justify-between ',
          s.content
        )}
      >
        <div>
          <p className="p-l dr-text-12 mobile-only">{content.topHeader}</p>

          {pathname !== '/vision' && (
            <TerminalAnimation
              id="header"
              data={headerText}
              className="p-l desktop-only"
            />
          )}
        </div>
        <SocialLinks story={story} className="desktop-only" />
      </div>
    </header>
  )
}

export function Footer({
  className,
  story,
}: { className?: string; story: ISbStoryData }) {
  const { content } = story

  return (
    <footer className={cn(className)}>
      <div className="flex flex-col dr-gap-4 dt:dr-gap-2 dr-mb-10">
        {content.bottomTexts.map(
          ({
            textBlock,
            _uid,
            _editable,
          }: SBBlock & {
            textBlock: StoryblokRichtext
          }) => (
            <div
              key={_uid}
              {...storyblokEditable({ _editable })}
              className="flex flex-col dr-gap-10"
            >
              <RichText content={textBlock} />
            </div>
          )
        )}
      </div>
      <Subscribe story={story} />
    </footer>
  )
}

export function SocialLinks({
  className,
  story,
}: { className?: string; story: ISbStoryData }) {
  const { content } = story

  return (
    <div
      className={cn('flex dr-gap-20 items-center justify-center', className)}
    >
      {content?.socialLinks?.map(({ text, url, _uid, icon }: SBLink) => {
        return (
          <Link key={_uid + text} href={url} className="dr-w-24 dr-h-24">
            <Image
              src={icon?.filename}
              alt={icon?.alt || ''}
              className="hover:brightness-120 transition-all duration-100"
            />
          </Link>
        )
      })}
    </div>
  )
}

function MenuCalendar({ className }: { className?: string }) {
  const [currentDate, setCurrentDate] = useState(new Date())

  useEffect(() => {
    const now = new Date()
    const delay = (60 - now.getSeconds()) * 1000 - now.getMilliseconds()

    const timeout = setTimeout(() => {
      setCurrentDate(new Date())

      const interval = setInterval(() => {
        setCurrentDate(new Date())
      }, 60000)

      return () => clearInterval(interval)
    }, delay)

    return () => clearTimeout(timeout)
  }, [])

  return (
    <ul
      className={cn('dr-mb-10 dt:dr-mb-20 flex flex-col dr-gap-2', className)}
    >
      <li>
        {'> current date:\u00A0'}
        <span className="text-[color:theme('colors.yellow')]">
          {currentDate?.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
          })}
        </span>
      </li>
      <li className="dr-mb-10">
        {'> current time:\u00A0'}
        <span className="text-[color:theme('colors.violet')]">
          {currentDate?.toLocaleTimeString('en-US', {
            hour: '2-digit',
            minute: '2-digit',
            hour12: true,
          })}
        </span>
      </li>
      <li>
        {'> overall status:\u00A0'}
        <span className="text-[color:theme('colors.green')]">operational</span>
      </li>
    </ul>
  )
}

function Subscribe({
  className,
  story,
}: { className?: string; story: ISbStoryData }) {
  const { content } = story

  return (
    <Form
      action={navigationAction}
      className={cn('flex flex-col dr-gap-5 dr-mt-20', className)}
    >
      <p className="p text-[color:theme('colors.white')]">
        {content?.subscribeHeader}
      </p>
      <Input
        label="" // bad for accessibility
        type="email"
        id="email"
        validation={validateEmail}
        required
        placeholder="type_"
        className={cn(
          'p lowercase dr-mt-6 dr-h-44 dr-px-16 dr-py-12',
          s['dots-pattern']
        )}
      />
      <SubmitButton
        textStates={{
          error: 'please try again',
          pending: 'escaping_',
          success: 'escaped_',
        }}
      >
        escape_
      </SubmitButton>
    </Form>
  )
}

function Borders({ className }: { className?: string }) {
  const [hasLoaded, setHasLoaded] = useState(false)

  useEffect(() => {
    setHasLoaded(true)
  }, [])

  return (
    <div className={cn(s.border, hasLoaded && s.loaded, className)}>
      <div className={s.vertical}>
        <span className={cn(s.line, s.top)} />
        <span className={cn(s.line, s.bottom)} />
      </div>
      <div className={s.horizontal} />
    </div>
  )
}

export function MobileSocialLinks(props: any) {
  const elementRef = useRef<HTMLDivElement>(null)

  useLenis(({ velocity }) => {
    const speed = Math.abs(velocity)

    if (speed !== 0) {
      elementRef.current?.classList?.toggle(s.hide, true)
    } else {
      elementRef.current?.classList?.toggle(s.hide, false)
    }
  })

  return (
    <div
      className={cn(
        'mobile-only h-full w-full absolute inset-0 pointer-events-none',
        s.socialLinks
      )}
      ref={elementRef}
    >
      <SocialLinks
        {...props}
        className="sticky top-[87.75dvh] left-1/2 -translate-x-1/2 z-2 w-fit! bg-[color:theme('colors.black')] dr-p-20 pointer-events-auto"
      />
    </div>
  )
}
